import { RotateCcw } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/Button";
import {
  FieldLabel,
  Select,
  Textarea,
} from "@/components/ui/Field";
import {
  CLIENT_NOTE_TEMPLATES,
  CLIENT_NOTE_TEMPLATE_OPTIONS,
  matchingClientNoteTemplate,
  type ClientNoteTemplateKey,
} from "@/features/invoices/client-note-templates";

interface Props {
  id: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

export function ClientFacingNotesField({
  id,
  value,
  onChange,
  disabled = false,
}: Props) {
  const [selectedTemplate, setSelectedTemplate] = useState<
    ClientNoteTemplateKey | "custom"
  >(() => matchingClientNoteTemplate(value));

  useEffect(() => {
    setSelectedTemplate(matchingClientNoteTemplate(value));
  }, [value]);

  const chooseTemplate = (
    next: ClientNoteTemplateKey | "custom",
  ) => {
    setSelectedTemplate(next);

    if (next !== "custom") {
      onChange(CLIENT_NOTE_TEMPLATES[next]);
    }
  };

  const resetTemplate = () => {
    if (selectedTemplate === "custom") {
      return;
    }

    onChange(CLIENT_NOTE_TEMPLATES[selectedTemplate]);
  };

  return (
    <div>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div className="min-w-0 flex-1">
          <FieldLabel htmlFor={`${id}-template`}>
            Client-facing note template
          </FieldLabel>
          <Select
            id={`${id}-template`}
            value={selectedTemplate}
            disabled={disabled}
            onChange={(event) =>
              chooseTemplate(
                event.target.value as
                  | ClientNoteTemplateKey
                  | "custom",
              )
            }
          >
            {CLIENT_NOTE_TEMPLATE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </div>

        <Button
          type="button"
          variant="secondary"
          size="sm"
          disabled={disabled || selectedTemplate === "custom"}
          onClick={resetTemplate}
        >
          <RotateCcw className="h-3.5 w-3.5" />
          Reset template
        </Button>
      </div>

      <div className="mt-3">
        <FieldLabel htmlFor={id}>Client-facing notes</FieldLabel>
        <Textarea
          id={id}
          value={value}
          disabled={disabled}
          placeholder="Write an optional note for the client…"
          onChange={(event) => {
            const nextValue = event.target.value;
            onChange(nextValue);
            setSelectedTemplate(
              matchingClientNoteTemplate(nextValue),
            );
          }}
        />
        <p className="mt-2 text-xs leading-5 text-ink-muted">
          Saved with this invoice and shown on the secure client page and invoice PDF.
        </p>
      </div>
    </div>
  );
}
